import { BATTLE_LOOP_NOTES, LOOP_DURATION_SEC, Note } from './battle-loop';
import { ACADEMY_LOOP_NOTES, ACADEMY_LOOP_DURATION_SEC } from './academy-loop';
import { BOSS_LOOP_NOTES, BOSS_LOOP_DURATION_SEC } from './boss-loop';

/**
 * MusicPlayer: BeepBox-style triangle-wave sequencer with 3-track support.
 *
 * Schedules notes via the Web Audio API (OscillatorNode + GainNode).
 * Each note uses a linear-ramp envelope: 20ms attack, sustain, 50ms release.
 * Each loop iteration applies a small velocity (gain) jitter ±0.05 so
 * repeated loops don't feel mechanical.
 *
 * Notes may specify an optional `wave` field ('triangle' | 'square' | 'sine').
 * Existing battle-loop notes without a wave field default to 'triangle'.
 *
 * Usage:
 *   const music = new MusicPlayer();
 *   music.start();           // starts battle BGM
 *   music.playBossBgm();     // switch mid-scene to boss track
 *   // ... later:
 *   music.stop();
 */
export class MusicPlayer {
  private ctx: AudioContext;
  private masterGain: GainNode;
  /**
   * Faction-BGM-variant tone-tilt filter. Sits between scheduled notes and
   * masterGain. May be null if the AudioContext stub lacks createBiquadFilter
   * (some test mocks). When null we connect notes straight to masterGain and
   * setFilter() is a no-op.
   */
  private filter: BiquadFilterNode | null = null;
  private scheduled: AudioScheduledSourceNode[] = [];
  private playing = false;
  private loopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private currentNotes: Note[] = BATTLE_LOOP_NOTES;
  private currentDurationSec: number = LOOP_DURATION_SEC;
  // Pitch multiplier applied to every scheduled note's freq. 1.0 = neutral.
  // Used by faction-BGM-variant feature; see faction-bgm-presets.ts.
  private pitchShift = 1.0;

  constructor(ctx?: AudioContext) {
    this.ctx = ctx ?? new AudioContext();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.35;
    // Insert filter between scheduled notes and masterGain when available.
    // Default settings: lowpass at 20kHz (transparent / no tilt).
    try {
      if (typeof (this.ctx as unknown as { createBiquadFilter?: () => BiquadFilterNode }).createBiquadFilter === 'function') {
        this.filter = this.ctx.createBiquadFilter();
        this.filter.type = 'lowpass';
        this.filter.frequency.value = 20_000;
        this.filter.Q.value = 0.707;
        this.filter.connect(this.masterGain);
      }
    } catch {
      this.filter = null;
    }
    this.masterGain.connect(this.ctx.destination);
  }

  start(notes: Note[] = BATTLE_LOOP_NOTES, loopDurationSec: number = LOOP_DURATION_SEC): void {
    if (this.playing) return;
    this.playing = true;
    this.currentNotes = notes;
    this.currentDurationSec = loopDurationSec;
    this.scheduleLoop(notes, loopDurationSec);
  }

  stop(): void {
    this.playing = false;
    if (this.loopTimeoutId !== null) {
      clearTimeout(this.loopTimeoutId);
      this.loopTimeoutId = null;
    }
    // Stop all in-flight oscillators
    for (const src of this.scheduled) {
      try { src.stop(); } catch { /* already stopped */ }
    }
    this.scheduled = [];
  }

  /**
   * Smoothly switch to a new track. Stops all current sources immediately
   * and reschedules the loop with the new notes + duration.
   */
  switchTrack(notes: Note[], loopDurationSec: number): void {
    // Clear pending loop-reschedule
    if (this.loopTimeoutId !== null) {
      clearTimeout(this.loopTimeoutId);
      this.loopTimeoutId = null;
    }
    // Stop all in-flight oscillators
    for (const src of this.scheduled) {
      try { src.stop(); } catch { /* already stopped */ }
    }
    this.scheduled = [];

    this.currentNotes = notes;
    this.currentDurationSec = loopDurationSec;

    if (this.playing) {
      this.scheduleLoop(notes, loopDurationSec);
    }
  }

  /** Switch to the academy (60 s C-major peaceful) track. */
  playAcademyBgm(): void {
    this.switchTrack(ACADEMY_LOOP_NOTES, ACADEMY_LOOP_DURATION_SEC);
  }

  /** Switch back to the default battle (30 s A-minor ostinato) track. */
  playBattleBgm(): void {
    this.switchTrack(BATTLE_LOOP_NOTES, LOOP_DURATION_SEC);
  }

  /** Switch to the boss (45 s A-minor high-tension) track. */
  playBossBgm(): void {
    this.switchTrack(BOSS_LOOP_NOTES, BOSS_LOOP_DURATION_SEC);
  }

  /** Set the master gain value (0..1). Applies immediately. */
  setMasterGain(value: number): void {
    this.masterGain.gain.value = Math.max(0, Math.min(1, value));
  }

  /**
   * Set a pitch multiplier applied to all subsequently scheduled notes' frequencies.
   * Clamped to [0.5, 2.0]; values outside that range are pinned. 1.0 = neutral.
   * Takes effect on the next loop iteration (currently scheduled notes keep their freq).
   */
  setPitchShift(ratio: number): void {
    this.pitchShift = Math.max(0.5, Math.min(2.0, ratio));
  }

  /** Current pitch shift ratio (1.0 = neutral). Exposed for tests / debug. */
  getPitchShift(): number {
    return this.pitchShift;
  }

  /**
   * Set faction-variant filter shape. `type === 'none'` collapses to a
   * transparent lowpass at 20kHz (audibly bypass). No-op when the filter
   * couldn't be created (test stub without createBiquadFilter).
   */
  setFilter(type: 'lowpass' | 'highpass' | 'none', cutoffHz: number, Q: number): void {
    if (!this.filter) return;
    if (type === 'none') {
      this.filter.type = 'lowpass';
      this.filter.frequency.value = 20_000;
      this.filter.Q.value = 0.707;
    } else {
      this.filter.type = type;
      this.filter.frequency.value = Math.max(20, Math.min(20_000, cutoffHz));
      this.filter.Q.value = Math.max(0.1, Math.min(20, Q));
    }
  }

  private scheduleLoop(notes: Note[], durationSec: number): void {
    if (!this.playing) return;

    const loopStart = this.ctx.currentTime;
    // Per-loop gain jitter ±0.05
    const jitter = (Math.random() * 0.10) - 0.05;

    for (const note of notes) {
      this.scheduleNote(note, loopStart, jitter);
    }

    // Queue next loop 10ms before this one ends to avoid gaps
    const nextLoopDelayMs = (durationSec - 0.01) * 1000;
    this.loopTimeoutId = setTimeout(() => {
      this.loopTimeoutId = null;
      // Always re-read current notes/duration in case switchTrack was called
      this.scheduleLoop(this.currentNotes, this.currentDurationSec);
    }, nextLoopDelayMs);
  }

  private scheduleNote(note: Note, loopStart: number, gainJitter: number): void {
    const attackSec  = 0.020; // 20ms
    const releaseSec = 0.050; // 50ms

    const noteStart = loopStart + note.startSec;
    const noteEnd   = noteStart + note.durationSec;
    const appliedGain = Math.max(0.01, Math.min(1.0, note.gain + gainJitter));

    // Oscillator — respect optional wave type (default: triangle).
    // Apply pitch shift (1.0 = neutral); used for faction BGM variant.
    const osc = this.ctx.createOscillator();
    osc.type = note.wave ?? 'triangle';
    osc.frequency.value = note.freq * this.pitchShift;

    // Envelope gain node
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0.0001, noteStart);
    env.gain.linearRampToValueAtTime(appliedGain, noteStart + attackSec);
    // Sustain until release begins
    env.gain.setValueAtTime(appliedGain, Math.max(noteStart + attackSec, noteEnd - releaseSec));
    env.gain.linearRampToValueAtTime(0.0001, noteEnd);

    // Route through faction filter when present, else straight to master gain.
    osc.connect(env).connect(this.filter ?? this.masterGain);
    osc.start(noteStart);
    osc.stop(noteEnd + 0.001); // tiny tail so stop doesn't clip

    this.scheduled.push(osc);
  }
}
